import type { UntitledIconData } from '@faceup/icons'
import {
  Route,
  type RouteCallback,
  type RouteProps,
  RouterRoutes,
  useGetRoute,
  useLocation,
  useNavigate,
} from '@faceup/router'
import { Skeleton, Tabs, type TabsProps } from '@faceup/ui-base'
import { getPathnameOnly } from '@faceup/utils'
import { Fragment, type ReactNode, Suspense, useEffect, useMemo, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { useFancyAppLoader } from '../FancyAppLoader'
import { ContentHeaderActionsContext, useIsAdminLayout } from './adminLayoutContext'
import { ContentHeader } from './ContentHeader'

const getRelativePathFromAbsolute: (path: string) => string = path => {
  const splittedPath = path.split('/')
  const lastPathSection = splittedPath[splittedPath.length - 1] ?? ''
  // Remove anchor from path
  return lastPathSection.split('#')[0] ?? ''
}

type BaseMenuItem = {
  title: ReactNode
  visible?: boolean
  key: string
  attributes?: NonNullable<TabsProps['items']>[0]['attributes']
  disabled?: boolean
}

type TabMenuItemPart = {
  // This is default type, so it's optional
  type?: 'tab'
  route: RouteCallback
  link?: never
  element: RouteProps['element']
}

type LinkMenuItemPart = {
  type: 'link'
  link: string
}

type ButtonMenuItemPart = {
  onClick: () => void
  icon?: UntitledIconData
  type: 'button'
  active?: boolean
}

type TabMenuItem = BaseMenuItem & TabMenuItemPart
type LinkMenuItem = BaseMenuItem & LinkMenuItemPart
type ButtonMenuItem = BaseMenuItem & ButtonMenuItemPart

export type MenuItemProp = TabMenuItem | LinkMenuItem | ButtonMenuItem

type ContentLayoutGlobalProps = {
  header: ReactNode
  banner?: ReactNode
  stretch?: boolean
}

type ContentLayoutProps = {
  menu?: MenuItemProp[]
  children?: ReactNode
} & ContentLayoutGlobalProps

export const ContentLayout = ({
  menu,
  children,
  header,
  banner,
  stretch = false,
}: ContentLayoutProps) => {
  const isAdminLayout = useIsAdminLayout()
  const [headerActionsSlot, setHeaderActionsSlot] = useState<HTMLElement | null>(null)
  const headerActions = useMemo(
    () => (isAdminLayout ? { slot: headerActionsSlot, attach: setHeaderActionsSlot } : null),
    [isAdminLayout, headerActionsSlot]
  )

  return (
    <ContentHeaderActionsContext.Provider value={headerActions}>
      <div className={stretch ? 'flex min-h-0 flex-1 flex-col' : 'contents'}>
        {banner}
        <div className='mb-8'>
          <div className='px-8 pt-8'>{header}</div>
          {menu && <ContentLayoutMenu menu={menu} />}
        </div>
        <div className={twMerge('mx-8 mb-8', stretch && 'flex min-h-0 flex-1 flex-col')}>
          {children}
          {menu && <ContentRouter menu={menu} />}
        </div>
      </div>
    </ContentHeaderActionsContext.Provider>
  )
}

const ContentLayoutMenu = ({ menu }: { menu: MenuItemProp[] }) => {
  const { pathname } = useLocation()
  const visibleMenuItems = getVisibleMenuItemsOnly(menu)
  const getRoute = useGetRoute()
  const navigate = useNavigate()

  const activeKey = visibleMenuItems.find(item => {
    if ('route' in item) {
      return getPathnameOnly(getRoute(item.route)) === pathname
    }
    if ('onClick' in item) {
      return item.active ?? false
    }
    return false
  })?.key

  useEffect(() => {
    // If there is no active key, navigate to the first visible item. This may happen if the user navigates to a route not allowed by permissions.
    const visibleItem = visibleMenuItems.find(item => item.visible !== false)
    if (!activeKey && !visibleItem?.type && visibleItem?.route) {
      navigate(visibleItem?.route)
    }
  }, [activeKey, navigate, visibleMenuItems])

  if (visibleMenuItems.length <= 1) {
    return null
  }

  return (
    <Tabs
      className='pt-6 *:px-8'
      activeKey={activeKey}
      size='small'
      items={visibleMenuItems.map(item => ({
        key: item.key,
        label: item.title,
        attributes: item.attributes,
        disabled: item.disabled,
      }))}
      onChange={key => {
        const item = visibleMenuItems.find(item => item.key === key)
        if (item?.type === 'link') {
          window.open(item.link, '_blank')
        } else if (item?.type === 'button') {
          item.onClick()
        } else {
          item?.route && navigate(item?.route)
        }
      }}
    />
  )
}

const ContentRouter = ({ menu }: { menu: MenuItemProp[] }) => {
  const visibleMenuItems = getVisibleMenuItemsOnly(menu)
  const getRoute = useGetRoute()

  return (
    <RouterRoutes>
      {visibleMenuItems.map(item => {
        if (item.type !== 'tab' && item.type !== undefined) {
          return null
        }
        const route = getRoute(item.route)
        const path = getRelativePathFromAbsolute(route)
        return (
          <Fragment key={path}>
            <Route path={path} element={<TabContentSuspense>{item.element}</TabContentSuspense>} />
          </Fragment>
        )
      })}
    </RouterRoutes>
  )
}

const getVisibleMenuItemsOnly = (menu: MenuItemProp[]) =>
  menu.filter(item => item.visible === undefined || item.visible)

ContentLayout.Header = ContentHeader

type TabContentSuspenseProps = {
  children: ReactNode
}
const TabContentSuspense = ({ children }: TabContentSuspenseProps) => {
  const location = useLocation()

  return (
    <Suspense fallback={<FancySkeleton />} key={location.pathname}>
      {children}
    </Suspense>
  )
}

const FancySkeleton = () => {
  const { show, hide } = useFancyAppLoader()
  // biome-ignore lint/correctness/useExhaustiveDependencies: Only run on mount and unmount for loader lifecycle
  useEffect(() => {
    show()
    return () => {
      hide()
    }
  }, [])
  return <Skeleton />
}
